/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ZAxis,
} from "recharts";
import apiUrls from "../../../../api";
import { formatContentTypeName } from "../../../graph/util/NameFormatter";
import SegmentedControl from "../../../../common/components/SegmentedControl/SegmentedControl";
import InfoCallout from "../../../../common/components/InfoCallout/InfoCallout";
import {
  FilterIcon as HeroFilterIcon,
  InformationCircleIcon,
  SearchIcon,
  XIcon,
} from "@heroicons/react/outline";
import { ExclamationIcon } from "@heroicons/react/solid";

const FilterIcon = () => <HeroFilterIcon className="h-4 w-4" />;

export interface InteractionStat {
  contentId: number;
  contentName: string;
  contentType: string;
  sceneId: number | null;
  sceneName: string | null;
  attemptCount: number;
  successRate: number;
  avgScore: number;
  scoreMax: number;
  avgDuration: number;
}

interface ScatterTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: InteractionStat }>;
  t: (key: string) => string;
  getSuccessRateColor: (rate: number) => string;
}

interface InteractionAnalysisTableProps {
  projectId: number;
  startDate?: string;
  endDate?: string;
  filterSceneId?: number | string | null;
  filterSceneName?: string | null;
  onClearFilter?: () => void;
  onSceneClick?: (sceneId: number, sceneName: string) => void;
  onInteractionClick?: (contentId: number, contentName: string, contentType: string) => void;
  /**
   * Variant determines which API endpoint to use:
   * - "creator": Uses interactionStats endpoint (all users' data, requires org access)
   * - "enduser": Uses enduserInteractionStats endpoint (only current user's data)
   */
  variant?: "creator" | "enduser";
}

const getSuccessRateColor = (rate: number): string => {
  if (rate >= 80) return "text-green-600";
  if (rate >= 50) return "text-yellow-600";
  return "text-red-600";
};

const getSuccessRateBg = (rate: number): string => {
  if (rate >= 80) return "bg-green-100";
  if (rate >= 50) return "bg-yellow-100";
  return "bg-red-100";
};

const getDotFillColor = (rate: number): string => {
  if (rate >= 80) return "#00c889";
  if (rate >= 50) return "#eab308";
  return "#ef4444";
};

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const InteractionAnalysisTable = ({
  projectId,
  startDate,
  endDate,
  filterSceneId,
  filterSceneName,
  onClearFilter,
  onSceneClick,
  onInteractionClick,
  variant = "creator",
}: InteractionAnalysisTableProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<InteractionStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortKey, setSortKey] = useState<"successRate" | "avgDuration" | "attemptCount">(
    "successRate",
  );
  const [sortAsc, setSortAsc] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "chart">("table");
  const fetchKeyRef = useRef<string | null>(null);

  const apiEndpoint =
    variant === "enduser" ? apiUrls.stats.enduserInteractionStats : apiUrls.stats.interactionStats;

  useEffect(() => {
    const fetchData = async () => {
      const fetchKey = `${variant}-${projectId}-${startDate || "null"}-${endDate || "null"}`;
      if (fetchKeyRef.current === fetchKey) return;
      fetchKeyRef.current = fetchKey;

      setIsLoading(true);
      try {
        let url = `${apiEndpoint}?project=${projectId}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;

        const response = await axios.get<InteractionStat[]>(url);
        setData(response.data || []);
      } catch (error) {
        console.error(`Error fetching ${variant} interaction stats:`, error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [projectId, startDate, endDate, apiEndpoint, variant]);

  const handleSort = (key: "successRate" | "avgDuration" | "attemptCount") => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === "successRate");
    }
  };

  const filteredData = useMemo(() => {
    const sceneFilteredData = filterSceneId
      ? data.filter((item) => item.sceneId === Number(filterSceneId))
      : data;

    if (!searchQuery) return sceneFilteredData;
    const needle = searchQuery.toLowerCase();
    return sceneFilteredData.filter(
      (item) =>
        item.contentName.toLowerCase().includes(needle) ||
        item.contentType.toLowerCase().includes(needle) ||
        (item.sceneName && item.sceneName.toLowerCase().includes(needle)),
    );
  }, [data, filterSceneId, searchQuery]);

  const sortedData = useMemo(
    () =>
      [...filteredData].sort((a, b) => {
        const multiplier = sortAsc ? 1 : -1;
        return (a[sortKey] - b[sortKey]) * multiplier;
      }),
    [filteredData, sortKey, sortAsc],
  );

  const renderScatterTooltip = (props: ScatterTooltipProps) => {
    const { active, payload } = props;
    if (!active || !payload?.[0]) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium text-gray-900">{d.contentName}</p>
        <p className="text-xs text-gray-500 mb-2">{d.sceneName || "-"}</p>
        <p className="text-gray-700">
          {t("statsNew.interactions.chartTooltipSuccessRate")}:{" "}
          <span className={`font-medium ${getSuccessRateColor(d.successRate)}`}>
            {d.successRate}%
          </span>
        </p>
        <p className="text-gray-700">
          {t("statsNew.interactions.chartTooltipAttempts")}:{" "}
          <span className="font-medium">{d.attemptCount}</span>
        </p>
      </div>
    );
  };

  const handleDotClick = (data: InteractionStat) => {
    if (onInteractionClick && data?.contentId) {
      onInteractionClick(data.contentId, data.contentName, data.contentType);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-500">
        {t("statsNew.charts.noData")}
      </div>
    );
  }

  if (filterSceneId && filteredData.length === 0) {
    return (
      <div className="space-y-4">
        <InfoCallout
          icon={<FilterIcon />}
          message={
            <>
              {t("statsNew.interactions.filteredBy")}: <strong>{filterSceneName}</strong>
            </>
          }
          action={
            onClearFilter && (
              <button
                onClick={onClearFilter}
                className="text-azure-600 hover:text-azure-800 text-sm underline"
              >
                {t("statsNew.interactions.showAll")}
              </button>
            )
          }
        />
        <div className="flex items-center justify-center h-32 text-gray-500">
          {t("statsNew.interactions.noInteractionsInScene")}
        </div>
      </div>
    );
  }

  const problematicCount = filteredData.filter((d) => d.successRate < 50).length;
  const avgSuccessRate =
    filteredData.length > 0
      ? Math.round(filteredData.reduce((sum, d) => sum + d.successRate, 0) / filteredData.length)
      : 0;

  return (
    <div className="space-y-4">
      {filterSceneId && (
        <InfoCallout
          icon={<FilterIcon />}
          message={
            <>
              {t("statsNew.interactions.filteredBy")}: <strong>{filterSceneName}</strong>
            </>
          }
          action={
            onClearFilter && (
              <button
                onClick={onClearFilter}
                className="text-azure-600 hover:text-azure-800 text-sm underline"
              >
                {t("statsNew.interactions.showAll")}
              </button>
            )
          }
        />
      )}

      <div className="flex items-center gap-6 text-sm">
        <div className="relative">
          <input
            type="text"
            placeholder={t("statsNew.interactions.search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        <div>
          <span className="text-gray-500">{t("statsNew.interactions.totalInteractions")}: </span>
          <span className="font-medium">{filteredData.length}</span>
        </div>
        <div className="relative group flex items-center gap-1">
          <span className="text-gray-500">{t("statsNew.charts.simpleAverage")}:</span>
          <span className={`font-medium ${getSuccessRateColor(avgSuccessRate)}`}>
            {avgSuccessRate}%
          </span>
          <InformationCircleIcon className="h-3.5 w-3.5 text-gray-400" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 w-48 text-center">
            {t("statsNew.charts.simpleAverageTooltip")}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>
        </div>
        {problematicCount > 0 && (
          <div className="flex items-center gap-1 text-red-600">
            <ExclamationIcon className="h-4 w-4" />
            <span className="font-medium">
              {problematicCount} {t("statsNew.interactions.problematic")}
            </span>
          </div>
        )}

        <div className="ml-auto">
          <SegmentedControl<"table" | "chart">
            options={[
              { value: "table", label: t("statsNew.interactions.tableView") },
              { value: "chart", label: t("statsNew.interactions.chartView") },
            ]}
            value={viewMode}
            onChange={setViewMode}
            variant="outlined"
          />
        </div>
      </div>

      {viewMode === "chart" ? (
        <div className="h-[400px] border border-gray-200 rounded-lg p-4 bg-white relative">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                dataKey="successRate"
                name="Success Rate"
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
                label={{
                  value: t("statsNew.interactions.successRate"),
                  position: "bottom",
                  offset: 10,
                  style: { fill: "#6b7280", fontSize: 12 },
                }}
              />
              <YAxis
                type="number"
                dataKey="attemptCount"
                name="Attempts"
                label={{
                  value: t("statsNew.interactions.attempts"),
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: "#6b7280", fontSize: 12 },
                }}
              />
              <ZAxis type="number" dataKey="avgDuration" range={[50, 400]} />
              <Tooltip
                content={(props) =>
                  renderScatterTooltip({ ...props, t, getSuccessRateColor } as ScatterTooltipProps)
                }
              />
              <Scatter
                data={filteredData}
                cursor="pointer"
                onClick={(data: InteractionStat) => handleDotClick(data)}
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={getDotFillColor(entry.successRate)}
                    opacity={0.8}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="overflow-auto h-[400px] rounded-lg border border-gray-200">
          <table className="min-w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-20 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.interaction")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.type")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                  {t("statsNew.interactions.scene")}
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("attemptCount")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.attempts")}
                    {sortKey === "attemptCount" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("successRate")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.avgScore")}
                    {sortKey === "successRate" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50 border-b border-gray-200"
                  onClick={() => handleSort("avgDuration")}
                >
                  <div className="flex items-center justify-center gap-1">
                    {t("statsNew.interactions.avgTime")}
                    {sortKey === "avgDuration" && <span>{sortAsc ? "↑" : "↓"}</span>}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {sortedData.length === 0 && searchQuery ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    {t("general.noResults")}
                  </td>
                </tr>
              ) : (
                sortedData.map((item) => (
                  <tr
                    key={item.contentId}
                    className={`${
                      item.successRate < 50 ? "bg-red-50 hover:bg-red-100" : "hover:bg-gray-50"
                    } ${onInteractionClick ? "cursor-pointer" : ""} transition-colors`}
                    onClick={() =>
                      onInteractionClick?.(item.contentId, item.contentName, item.contentType)
                    }
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b border-gray-200">
                      {item.contentName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 border-b border-gray-200">
                      {formatContentTypeName(item.contentType)}
                    </td>
                    <td className="px-4 py-3 text-sm border-b border-gray-200">
                      {item.sceneId && item.sceneName && onSceneClick ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.sceneId && item.sceneName) {
                              onSceneClick(item.sceneId, item.sceneName);
                            }
                          }}
                          className="group flex items-center gap-1 text-gray-700 hover:text-green-600 text-left transition-colors"
                        >
                          <span>{item.sceneName}</span>
                          <HeroFilterIcon className="h-3.5 w-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ) : (
                        <span className="text-gray-500">{item.sceneName || "-"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-center border-b border-gray-200">
                      {item.attemptCount}
                    </td>
                    <td className="px-4 py-3 text-center border-b border-gray-200">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSuccessRateBg(
                          item.successRate,
                        )} ${getSuccessRateColor(item.successRate)}`}
                      >
                        {item.successRate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-center border-b border-gray-200">
                      {formatDuration(item.avgDuration)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InteractionAnalysisTable;

/**
 * @deprecated Use InteractionAnalysisTable with variant="enduser" instead
 * Kept for backward compatibility
 */
export const EnduserInteractionAnalysisTable = (
  props: Omit<InteractionAnalysisTableProps, "variant">,
) => <InteractionAnalysisTable {...props} variant="enduser" />;
